import React, { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Copy, Check } from 'lucide-react';
import { toast } from 'sonner';

export default function DocsJSAPI() {
  const [copiedCode, setCopiedCode] = useState(null);

  const copyToClipboard = (code, id) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(id);
    toast.success('Copied to clipboard');
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const apiMethods = [
    {
      name: "ci('init', config)",
      description: 'Initialize the Commerce Index client with your brand credentials.',
      params: [
        { name: 'brandId', type: 'string', required: true, desc: 'Your assigned brand identifier' },
        { name: 'apiKey', type: 'string', required: false, desc: 'Optional API key for server-side validation' },
        { name: 'debug', type: 'boolean', required: false, desc: 'Enable console logging for debugging' }
      ],
      example: `ci('init', {
  brandId: 'brand_xyz',
  debug: true  // Enable for development
});`
    },
    {
      name: "ci('trackView', payload)",
      description: 'Track a product page view. Call this when a user loads a PDP.',
      params: [
        { name: 'productId', type: 'string', required: true, desc: 'Your internal product identifier' },
        { name: 'productSku', type: 'string', required: false, desc: 'Product SKU or variant ID' },
        { name: 'productTitle', type: 'string', required: false, desc: 'Product title for debugging' },
        { name: 'productUrl', type: 'string', required: false, desc: 'Full URL of the product page' }
      ],
      example: `ci('trackView', {
  productId: 'prod_123',
  productSku: 'SKU-123-RED-M',
  productTitle: 'Red Performance Legging',
  productUrl: window.location.href
});`
    },
    {
      name: "ci('trackPurchase', payload)",
      description: 'Track a completed purchase. Call this on the order confirmation page.',
      params: [
        { name: 'orderId', type: 'string', required: true, desc: 'Your internal order identifier' },
        { name: 'currency', type: 'string', required: true, desc: 'ISO 4217 currency code (USD, EUR, etc.)' },
        { name: 'items', type: 'array', required: true, desc: 'Array of purchased items' },
        { name: 'totalValue', type: 'number', required: false, desc: 'Total order value for analytics' }
      ],
      example: `ci('trackPurchase', {
  orderId: 'order_789',
  currency: 'USD',
  totalValue: 129.97,
  items: [
    {
      productId: 'prod_123',
      productSku: 'SKU-123-RED-M',
      productTitle: 'Red Performance Legging',
      quantity: 2,
      value: 39.99
    },
    {
      productId: 'prod_456',
      productSku: 'SKU-456-BLACK',
      productTitle: 'Black Sports Bra',
      quantity: 1,
      value: 49.99
    }
  ]
});`
    }
  ];

  return (
    <div className="max-w-4xl">
      <div className="mb-8">
        <Badge className="bg-[#7A5CFF]/20 text-[#7A5CFF] border-[#7A5CFF]/30 mb-4">
          Integration
        </Badge>
        <h1 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
          JavaScript API
        </h1>
        <p className="text-lg text-[#94A3B8]">
          Browser snippet reference for the ci.js library.
        </p>
      </div>

      {/* How it Works */}
      <section className="mb-12">
        <h2 className="text-xl font-semibold text-white mb-4">How It Works</h2>
        <div className="space-y-3 text-[#94A3B8]">
          <p>
            The <code className="text-[#3EF1C7] bg-[#3EF1C7]/10 px-1 rounded">ci.js</code> library provides a simple, 
            function-based API for tracking events. It:
          </p>
          <ul className="list-disc list-inside space-y-1 ml-4">
            <li>Loads asynchronously to avoid blocking page rendering</li>
            <li>Queues commands until fully loaded, then replays them</li>
            <li>Automatically generates session IDs for grouping</li>
            <li>Adds timestamps and environment info to events</li>
            <li>Handles retries on transient failures (coming soon)</li>
          </ul>
        </div>
      </section>

      {/* API Methods */}
      <section className="space-y-8">
        <h2 className="text-xl font-semibold text-white mb-4">API Methods</h2>
        
        {apiMethods.map((method, index) => (
          <Card key={index} className="bg-[#0D1628] border-[#22304A]">
            <CardHeader>
              <CardTitle className="text-white font-mono text-lg">
                {method.name}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-[#94A3B8]">{method.description}</p>
              
              <div>
                <h4 className="text-white font-medium mb-2">Parameters</h4>
                <div className="space-y-2">
                  {method.params.map((param, i) => (
                    <div key={i} className="flex items-start gap-3 py-2 border-b border-[#22304A] last:border-0">
                      <code className="text-[#3EF1C7] text-sm bg-[#3EF1C7]/10 px-2 py-0.5 rounded flex-shrink-0">
                        {param.name}
                      </code>
                      <span className="text-[#7A5CFF] text-sm">{param.type}</span>
                      {param.required && (
                        <Badge className="bg-[#FFD166]/20 text-[#FFD166] border-[#FFD166]/30 text-xs">
                          Required
                        </Badge>
                      )}
                      <span className="text-[#94A3B8] text-sm">{param.desc}</span>
                    </div>
                  ))}
                </div>
              </div>
              
              <div>
                <h4 className="text-white font-medium mb-2">Example</h4>
                <div className="relative">
                  <pre className="bg-[#0B1120] border border-[#334155] rounded-lg p-4 overflow-x-auto text-sm text-[#E2E8F0]">
                    <code>{method.example}</code>
                  </pre>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="absolute top-2 right-2 text-[#64748B] hover:text-white"
                    onClick={() => copyToClipboard(method.example, `method-${index}`)}
                  >
                    {copiedCode === `method-${index}` ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </section>

      {/* Item Schema */}
      <section className="mt-12">
        <h2 className="text-xl font-semibold text-white mb-4">Item Schema (for trackPurchase)</h2>
        <Card className="bg-[#0D1628] border-[#22304A]">
          <CardContent className="pt-6">
            <div className="space-y-2">
              {[
                { name: 'productId', type: 'string', required: true, desc: 'Must match product_id from view events' },
                { name: 'productSku', type: 'string', required: false, desc: 'SKU or variant identifier' },
                { name: 'productTitle', type: 'string', required: false, desc: 'Product title' },
                { name: 'quantity', type: 'number', required: false, desc: 'Number purchased (default: 1)' },
                { name: 'value', type: 'number', required: false, desc: 'Unit price for analytics' }
              ].map((param, i) => (
                <div key={i} className="flex items-start gap-3 py-2 border-b border-[#22304A] last:border-0">
                  <code className="text-[#3EF1C7] text-sm bg-[#3EF1C7]/10 px-2 py-0.5 rounded flex-shrink-0">
                    {param.name}
                  </code>
                  <span className="text-[#7A5CFF] text-sm">{param.type}</span>
                  {param.required && (
                    <Badge className="bg-[#FFD166]/20 text-[#FFD166] border-[#FFD166]/30 text-xs">
                      Required
                    </Badge>
                  )}
                  <span className="text-[#94A3B8] text-sm">{param.desc}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </section>

      {/* Version */}
      <section className="mt-12">
        <div className="p-4 bg-[#1E293B] rounded-lg border border-[#334155]">
          <p className="text-[#94A3B8] text-sm">
            <strong className="text-white">Current Version:</strong> ci.js v1.0.0
          </p>
          <p className="text-[#64748B] text-sm mt-1">
            Check <code className="text-[#3EF1C7]">ci.version</code> in console to verify loaded version.
          </p>
        </div>
      </section>
    </div>
  );
}
